import React from 'react';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { Copy, Flag, Star } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { getClientFacingOrderStatus } from '../../helpers/constants';
import { getInvoiceNumbersForOrder } from '../../helpers/collectedFees';
import { copyToClipboard, formatCurrency, getFullAddress, isClient, isStaff } from '../../helpers/global';
import { getBgColorFromStatus } from '../../helpers/ui';

const NON_STAFF_HIDDEN_KEYS = [
  'pin',
  'client',
  'serviceAccurateId',
  'infoRequestedDate',
  'infoReceivedDate',
  'readyForQADate',
  'reportSentDate',
  'revisionDate',
  'municipality',
  'service.county',
  'assignedProcessors.0.name',
  'hardCost',
  'invoiceNumber',
  'invoiceAmount'
];

export const getOrderTableColumns = ({ user, onTogglePin, orderActionLoading = {} }) => {
  const columns = [
    {
      key: 'pin',
      label: '',
      sortable: false,
      align: 'center',
      width: '50px',
      style: { padding: '12px 4px' },
      render: (_, row) => {
        const isPinning = orderActionLoading[row._id] === 'pin';

        if (isPinning) {
          return (
            <Spinner
              animation="border"
              size="sm"
              role="status"
              style={{ width: 16, height: 16, borderWidth: 2 }}
            />
          );
        }

        return (
          <Star
            size={16}
            fill={row.pinned ? '#F5A623' : 'none'}
            color={row.pinned ? '#F5A623' : '#9CA3AF'}
            className="flex-shrink-0"
            style={{ cursor: orderActionLoading[row._id] ? 'not-allowed' : 'pointer' }}
            onClick={e => {
              e.stopPropagation();
              if (orderActionLoading[row._id]) return;
              onTogglePin?.(row);
            }}
          />
        );
      }
    },
    {
      key: 'propertyAddress',
      label: 'Property Address',
      sortable: true,
      align: 'left',
      width: '200px',
      render: (value, row) => {
        const fullAddress = row?.service ? getFullAddress(row?.service) : '';
        const tooltipId = `tooltip-address-${row?._id || row?.service?._id || 'row'}`;
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{fullAddress}</Tooltip>}>
            <div className="d-flex align-items-center justify-content-start">
              <span className="d-inline-block text-truncate font-weight-semibold" style={{ maxWidth: '100%' }}>
                {fullAddress}
              </span>
              {row?.rush && 
                <span
                  style={{
                    padding: '1px 6px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                  className="text-danger bg-danger-light rounded-pill mx-1"
                >
                  RUSH
                </span>  
              }
            </div>
          </OverlayTrigger>
        );
      }
    },
    {
      key: 'fileName',
      label: 'File Name',
      sortable: true,
      align: 'left',
      width: '120px',
      render: (value, row) => {
        const name = row?.service?.fileName || 'N/A';
        const tooltipId = `tooltip-filename-${row?._id || row?.service?._id || 'row'}`;
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{name}</Tooltip>}>
            <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
              {name}
            </span>
          </OverlayTrigger>
        );
      }
    },
    {
      key: 'orderType',
      label: 'Service',
      sortable: true,
      align: 'left',
      width: '160px',
      render: (value, row) => {
        const serviceColors = {
          'Lien Search': '#f59e0b',
          Estoppel: '#a855f7',
          Survey: '#10b981'
        };
        const bgColor = serviceColors[value] || 'primary';
        return (
          <div className="d-flex align-items-center justify-content-start gap-1">
            <div
              style={{
                width: '10px',
                height: '10px',
                minWidth: '10px',
                minHeight: '10px',
                borderRadius: '50%',
                display: 'inline-block',
                verticalAlign: 'middle',
                backgroundColor: bgColor
              }}
            />
            <div className="text-dark text-truncate ml-2 font-weight-semibold" style={{ maxWidth: '100%' }}>
              {value}
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Order Status',
      sortable: true,
      align: 'left',
      width: '130px',
      color: (value, row) => {
        if (isClient(user?.role)) {
          return getClientFacingOrderStatus(value, row?.cancellationRequested);
        }
        return value;
      },
      style: (value, row) => {
        const statusForColor = isClient(user?.role)
          ? getClientFacingOrderStatus(value, row?.cancellationRequested)
          : value;
        const bgColor = getBgColorFromStatus(statusForColor);
        return { backgroundColor: bgColor?.bg };
      },
      render: (value, row) => {
        const status = isClient(user?.role)
          ? getClientFacingOrderStatus(value, row?.cancellationRequested)
          : value;
        const bgColor = getBgColorFromStatus(status);
        return (
          <div className="d-flex align-items-center justify-content-start gap-1">
            <div className="text-truncate ml-2 font-weight-bold" style={{ maxWidth: '100%', color: bgColor?.text }}>
              {status === 'Completed/Unpaid' ? (
                <>
                  Completed/<span className="text-danger">Unpaid</span>
                </>
              ) : (
                status
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'estimatedTurnAroundTime',
      label: 'ETA',
      sortable: true,
      align: 'center',
      width: '120px',
      render: (value, row) => {
        const eta = value ? moment(value) : null;
        const closingDate = row?.service?.closingDate ? moment(row.service.closingDate) : null;
        const showFlag = eta && closingDate && eta.isSameOrAfter(closingDate, 'day');
        return (
          <div className="d-flex align-items-center justify-content-start">
            {value ? moment(value).format('MM/DD/YYYY') : '-'}
            {showFlag && (
              <OverlayTrigger placement="top" overlay={<Tooltip>ETA date equals or exceeds the closing date</Tooltip>}>
                <Flag
                  size={16}
                  className="flex-shrink-0"
                  style={{ fill: '#FACC15', color: '#FACC15', marginLeft: '4px' }}
                />
              </OverlayTrigger>
            )}
          </div>
        );
      }
    },
    {
      key: 'service.closingDate',
      label: 'Closing Date',
      sortable: true,
      align: 'center',
      width: '120px',
      render: (value, row) => (row?.service?.closingDate ? moment(row.service.closingDate).format('MM/DD/YYYY') : '')
    },
    {
      key: 'createdAt',
      label: 'Order Date',
      sortable: true,
      align: 'center',
      width: '120px',
      type: 'date',
      dateFormat: 'MM/DD/YYYY'
    },
    {
      key: 'accurateId',
      label: 'Service No',
      sortable: true,
      align: 'center',
      width: '100px',
      render: v => <span>{v}</span>
    },
    {
      key: 'serviceAccurateId',
      label: 'File No',
      sortable: true,
      align: 'center',
      width: '120px',
      render: (v, r) => (
        <span>
          {r?.service?.accurateId}{' '}
          <Copy
            className="ml-1 hover-text-primary"
            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
            size={14}
            onClick={e => {
              e.stopPropagation();
              copyToClipboard(r?.service?.accurateId);
              toast.success('File ID copied to clipboard!');
            }}
          />
        </span>
      )
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      align: 'left',
      width: '150px',
      render: (value, row) => {
        const clientName = row?.service?.organization?.name || '-';
        const tooltipId = `tooltip-client-${row?._id || row?.service?._id || 'row'}`;
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{clientName}</Tooltip>}>
            <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
              {clientName}
            </span>
          </OverlayTrigger>
        );
      }
    },
    // bydefault hidden columns
    {
      key: 'infoRequestedDate',
      label: 'Date In Info Requested',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => (value ? moment(value).format('MM/DD/YYYY') : '-')
    },
    {
      key: 'infoReceivedDate',
      label: 'Date In Info Received',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => (value ? moment(value).format('MM/DD/YYYY') : '-')
    },
    {
      key: 'readyForQADate',
      label: 'Date Ready for QA',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => (value ? moment(value).format('MM/DD/YYYY') : '-')
    },
    {
      key: 'reportSentDate',
      label: 'Date Report Sent',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => (value ? moment(value).format('MM/DD/YYYY') : '-')
    },
    {
      key: 'revisionDate',
      label: 'Date In Revision',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => (value ? moment(value).format('MM/DD/YYYY') : '-')
    },
    {
      key: 'municipality',
      label: 'Municipality',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: value => value || '-'
    },
    {
      key: 'service.county',
      label: 'County',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '130px',
      render: (value, row) => row?.service?.county || '-'
    },
    {
      key: 'assignedProcessors.0.name',
      label: 'Assigned Processor',
      hiddenByDefault: true,
      sortable: true,
      align: 'center',
      width: '150px',
      render: (value, row) => {
        const processors = row?.assignedProcessors?.map(p => p.name);
        return processors?.length > 0 ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-processors-${row?._id || 'row'}`}>{processors.join(', ')}</Tooltip>}
          >
            <span className="d-inline-block text-truncate" style={{ maxWidth: '100%', cursor: 'pointer' }}>
              {processors.join(', ')}
            </span>
          </OverlayTrigger>
        ) : (
          '-'
        );
      }
    },
    {
      key: 'hardCost',
      label: 'Hard Cost',
      hiddenByDefault: true,
      sortable: true,
      align: 'right',
      width: '110px',
      render: value => formatCurrency(value)
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice Number',
      hiddenByDefault: true,
      sortable: false,
      align: 'left',
      width: '160px',
      render: (_, row) => {
        const numbers = getInvoiceNumbersForOrder(row);
        if (!numbers) return '-';
        const tooltipId = `tooltip-invoice-numbers-${row?._id || 'row'}`;
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{numbers}</Tooltip>}>
            <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
              {numbers}
            </span>
          </OverlayTrigger>
        );
      }
    },
    {
      key: 'invoiceAmount',
      label: 'Invoice Amount',
      hiddenByDefault: true,
      sortable: true,
      align: 'right',
      width: '130px',
      render: value => formatCurrency(value)
    }
  ];

  if (!isStaff(user?.role)) {
    return columns.filter(col => !NON_STAFF_HIDDEN_KEYS.includes(col.key));
  }

  return columns;
};

export const pickOrderColumnsByKeys = (allColumns, selectedKeys) => {
  const byKey = Object.fromEntries(allColumns.map(col => [col.key, col]));
  return selectedKeys.map(key => byKey[key]).filter(Boolean);
};
